import React, { useEffect, useState } from "react";
import {
  Table,
  Divider,
  PageHeader,
  Button,
  Form,
  Select,
  Input,
  message,
  Modal,
  Segmented,
  Radio,
  Checkbox,
  DatePicker,
  Image,
} from "antd";
import {
  FlexRowContainer,
  FlexRowEndContainer,
  FlexRowQuarterContainer,
  FlexRowReverseContainer,
} from "../../Common/Container.styles";

import { useNavigate } from "react-router-dom";
import { SearchOutlined } from "@ant-design/icons";

import * as settingAPI from "../../../api/setting";

import {
  dateToCompactString,
  dateToString,
  numberWithCommas,
} from "../../../util/common";

import Pagination from "react-js-pagination";
import { Option } from "antd/lib/mentions";
import { useRecoilState } from "recoil";
import { searchItemState } from "../../../states/atoms";
import TextArea from "antd/lib/input/TextArea";

export default function BannerList() {
  const [searchItem, setSearchItem] = useRecoilState(searchItemState);

  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [banners, setBanners] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);

  const [current, setCurrent] = useState(
    Number(sessionStorage.getItem("page")) ?? 1
  );
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState("전체");

  const [startCreatedDate, setStartCreatedDate] = useState(null);
  const [endCreatedDate, setEndCreatedDate] = useState(null);

  const [showRefundModal, setShowRefundModal] = useState(false);

  const onChange = (page) => {
    setCurrent(page);
    sessionStorage.setItem("page", page);
  };

  const [form] = Form.useForm();

  const searchQuery = Form.useWatch("searchQuery", form);
  const searchKeyword = Form.useWatch("searchKeyword", form);
  const status = Form.useWatch("status", form);
  const bannering = Form.useWatch("bannering", form);
  const size = Form.useWatch("size", form);

  const fetchBanners = () => {
    const request = {
      page: current,
      size: size ?? 50,
      startCreatedDate,
      endCreatedDate,
      status,
      bannering,
      isPopup: false,
    };
    request[`${searchQuery}`] = searchKeyword;
    request[`${searchItem.searchQuery}`] = searchItem.searchKeyword;

    settingAPI.getBanners(request).then((res) => {
      if (res.data.linusCode === 1) {
        setTotal(res.data.page.count);
        setBanners(
          res.data.data?.map((r) => {
            return {
              ...r,
              key: r.id,
              regTime: dateToString(r.regTime),
              last_login: dateToString(r.last_login),
            };
          })
        );
        setLoading(false);
      } else {
        alert(res.data.data.msg);
      }
    });
  };

  useEffect(() => {
    if (current !== 1) {
      setCurrent(1);
      return;
    }

    setLoading(true);
    fetchBanners();
  }, [filter, startCreatedDate, endCreatedDate, status, bannering, size]);

  useEffect(() => {
    setLoading(true);
    fetchBanners();
  }, [current]);

  const onSearch = (e) => {
    if (!searchQuery) {
      message.error("검색조건을 선택해주세요.");
      return;
    }

    if (!searchKeyword) {
      message.error("검색 키워드를 입력해주세요.");
      return;
    }

    const newSearchItem = {
      ...searchItem,
      searchQuery: searchQuery,
      searchKeyword: searchKeyword,
    };
    setSearchItem(newSearchItem);

    setCurrent(1);
    sessionStorage.setItem("page", 1);

    setLoading(true);
    fetchBanners();
  };

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedItems(selectedRows);
    },
    getCheckboxProps: (record) => ({
      disabled: record.name === "Disabled Banner",
      // Column configuration not to be checked
      name: record.name,
    }),
  };

  // 주문번호, 닉네임, 가입채널, 가입년월일시, 최종로그인년월일시, 마케팅동의여부
  const columns = [
    { title: "ID", dataIndex: "id" },
    { title: "이름", dataIndex: "name" },
    { title: "제목", dataIndex: "title" },
    { title: "내용", dataIndex: "content" },
    { title: "순서", dataIndex: "index" },
    {
      title: "생성일",
      dataIndex: "createdDate",
      width: "10%",
      render: (val) => {
        return dateToString(val);
      },
    },
    {
      title: "수정일",
      dataIndex: "updatedDate",
      width: "10%",
      render: (val) => {
        return dateToString(val);
      },
    },
  ];

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);

  const onDelete = (e) => {
    e.preventDefault();
    if (selectedItems.length === 0) {
      message.error("선택된 항목이 없습니다.");
      return;
    }
    setLoading(true);
    settingAPI
      .deleteBanners({ selectedItems: selectedItems?.map((i) => i.id) })
      .then((res) => {
        if (res.data.linusCode === 1) {
          fetchBanners();
          setSelectedItems([]);
          message.success("성공적으로 삭제되었습니다.");
          setDeleteModalVisible(false);
        } else {
          alert(res.data.data.msg);
        }
      });
  };

  const onShowDeleteModal = (e) => {
    e.preventDefault();
    setDeleteModalVisible(true);
  };

  const onEdit = (e) => {
    e.preventDefault();

    if (selectedItems.length !== 1) {
      message.error("하나의 항목을 선택 후 수정 버튼을 눌러주세요.");
      return;
    }

    navigate(`/setting/banner/edit/${selectedItems[0].id}`);
  };

  const [refundForm] = Form.useForm();

  return (
    <div>
      <Modal
        width={400}
        title="삭제 확인"
        visible={deleteModalVisible}
        onOk={onDelete}
        onCancel={() => {
          setDeleteModalVisible(false);
        }}
        footer={[
          <Button
            key="back"
            onClick={(e) => {
              e.preventDefault();
              setDeleteModalVisible(false);
            }}
          >
            취소
          </Button>,
          <Button key="submit" type="primary" danger onClick={onDelete}>
            삭제
          </Button>,
        ]}
      >
        선택한 항목을 삭제하시겠습니까?
      </Modal>
      <PageHeader
        className="site-page-header"
        title="배너 목록"
        subTitle={`총 ${total}개의 배너가 존재합니다.`}
      />
      <Form form={form} onFinish={onSearch}>
        <FlexRowContainer style={{ marginTop: "10px" }}>
          <FlexRowQuarterContainer></FlexRowQuarterContainer>
        </FlexRowContainer>
        <FlexRowReverseContainer>
          <FlexRowQuarterContainer>
            <Form.Item name="size" style={{ width: "300px" }}>
              <Select placeholder="표시 개수 선택">
                {[
                  { label: "50개씩 보기", value: 50 },
                  { label: "100개씩 보기", value: 100 },
                  { label: "200개씩 보기", value: 200 },
                  { label: "300개씩 보기", value: 300 },
                  { label: "400개씩 보기", value: 400 },
                  { label: "500개씩 보기", value: 500 },
                ].map((c) => {
                  return (
                    <Select.Option key={c.label} value={c.value}>
                      {c.label}
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>
          </FlexRowQuarterContainer>
        </FlexRowReverseContainer>
      </Form>
      <FlexRowReverseContainer>
        {/*<Button danger onClick={onShowDeleteModal}>*/}
        {/*  선택 삭제*/}
        {/*</Button>*/}
        {/*<Button style={{ margin: "0 10px" }} onClick={onEdit}>*/}
        {/*  선택 수정*/}
        {/*</Button>*/}
        <Button
          style={{ margin: "0 0 0 10px" }}
          type="primary"
          onClick={() => {
            navigate(`/setting/banner/new`);
          }}
        >
          추가
        </Button>
      </FlexRowReverseContainer>
      <Divider />
      <Table
        pagination={false}
        loading={loading}
        columns={columns}
        dataSource={banners}
        rowSelection={{
          type: "checkbox",
          ...rowSelection,
        }}
        onRow={(record, rowIndex) => {
          return {
            onClick: (event) => {
              navigate(`/setting/banner/edit/${record.id}/`);
            }, // click row
            onDoubleClick: (event) => {}, // double click row
            onContextMenu: (event) => {}, // right button click row
            onMouseEnter: (event) => {}, // mouse enter row
            onMouseLeave: (event) => {}, // mouse leave row
          };
        }}
      />
      <FlexRowEndContainer style={{ marginTop: "30px" }}>
        <Pagination
          activePage={current}
          itemsCountPerPage={size ?? 50}
          totalItemsCount={total}
          pageRangeDisplayed={10}
          onChange={onChange}
        />
      </FlexRowEndContainer>
    </div>
  );
}
